import { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SOCIAL_PLATFORMS } from './social-platforms';
import {
  normalizeSocialUrl,
  type SocialPlatform,
  type SocialValidationResult,
} from '@/lib/social-links';

interface SocialLinksEditorProps {
  value?: Record<string, string> | null;
  onChange: (val: Record<string, string>) => void;
}

export function SocialLinksEditor({ value, onChange }: SocialLinksEditorProps) {
  const [links, setLinks] = useState<Record<string, string>>(value ?? {});
  const [prevValue, setPrevValue] = useState(value);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  if (prevValue !== value) {
    setPrevValue(value);
    setLinks(value ?? {});
  }

  const handleChange = (platform: SocialPlatform, val: string) => {
    const updated = { ...links, [platform]: val };
    setLinks(updated);
    if (touched[platform]) {
      validateAndNotify(platform, val, updated);
    }
  };

  const handleBlur = (platform: SocialPlatform) => {
    setTouched((prev) => ({ ...prev, [platform]: true }));
    validateAndNotify(platform, links[platform] ?? '', links);
  };

  const validateAndNotify = (
    platform: SocialPlatform,
    val: string,
    currentLinks: Record<string, string>
  ) => {
    const newErrors = { ...errors };

    if (!val.trim()) {
      delete newErrors[platform];
      setErrors(newErrors);
      const cleaned = Object.fromEntries(
        Object.entries(currentLinks).filter(([, v]) => v.trim().length > 0)
      );
      onChange(cleaned);
      return;
    }

    const result: SocialValidationResult = normalizeSocialUrl(platform, val);
    if (!result.ok) {
      newErrors[platform] = result.error;
      setErrors(newErrors);
      return;
    }

    delete newErrors[platform];
    setErrors(newErrors);
    const updated = { ...currentLinks, [platform]: result.url };
    setLinks(updated);
    const cleaned = Object.fromEntries(
      Object.entries(updated).filter(([, v]) => v.trim().length > 0)
    );
    onChange(cleaned);
  };

  const hasError = useCallback((platform: SocialPlatform) => Boolean(errors[platform]), [errors]);

  return (
    <div className="space-y-3">
      {SOCIAL_PLATFORMS.map(({ key, label, icon: Icon, color }) => (
        <div key={key} className="space-y-1.5">
          <Label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Icon className="h-4 w-4" style={{ color }} />
            {label}
          </Label>
          <Input
            type="text"
            value={links[key] ?? ''}
            onChange={(e) => handleChange(key, e.target.value)}
            onBlur={() => handleBlur(key)}
            placeholder={`${label} profile URL or username`}
            className="rounded-xl"
            aria-invalid={hasError(key)}
          />
          {hasError(key) && <p className="text-xs text-destructive font-medium">{errors[key]}</p>}
        </div>
      ))}
    </div>
  );
}
